import { useEffect, useMemo, useState } from "react";
import { http, fmtINR, fmtNum, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Save, Send, CheckCircle2, FileClock, XCircle, Store as StoreIcon } from "lucide-react";

const CATEGORIES = ["DENIM", "NON DENIM", "KNITS", "ACCESSORIES"];

const STATUS_STYLES = {
    Submitted: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
    Draft: "bg-amber-500/10 text-amber-600 border-amber-500/30",
    Reopened: "bg-blue-500/10 text-blue-600 border-blue-500/30",
    NotSubmitted: "bg-red-500/10 text-red-600 border-red-500/30",
};

export default function DailySales() {
    const [stores, setStores] = useState([]);
    const [outletId, setOutletId] = useState("");
    const [saleDate, setSaleDate] = useState(new Date().toISOString().slice(0, 10));
    const [lines, setLines] = useState(CATEGORIES.map((c) => ({ category: c, qty: 0 })));
    const [freshQty, setFreshQty] = useState(0);
    const [freshValue, setFreshValue] = useState(0);
    const [eossQty, setEossQty] = useState(0);
    const [eossValue, setEossValue] = useState(0);
    const [mrpValue, setMrpValue] = useState(0);
    const [netValue, setNetValue] = useState(0);
    const [saving, setSaving] = useState(false);
    const [statusPayload, setStatusPayload] = useState(null);

    const outlet = useMemo(() => stores.find((s) => s.id === outletId), [stores, outletId]);
    const totalQty = useMemo(
        () => lines.reduce((a, l) => a + Number(l.qty || 0), 0),
        [lines]
    );

    useEffect(() => {
        http.get("/masters/stores").then((r) => {
            setStores(r.data);
            if (r.data.length && !outletId) setOutletId(r.data[0].id);
        });
    }, []);

    useEffect(() => {
        if (!saleDate) return;
        http
            .get("/spykar/daily-sales/submission-status", { params: { sale_date: saleDate } })
            .then((r) => setStatusPayload(r.data))
            .catch(() => setStatusPayload(null));
    }, [saleDate, saving]);

    const setLineQty = (idx, val) => {
        const next = [...lines];
        next[idx] = { ...next[idx], qty: Number(val || 0) };
        setLines(next);
    };

    const submit = async (finalize) => {
        if (!outletId || !saleDate) {
            toast.error("Select outlet and date");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                outlet_id: outletId,
                outlet_name: outlet?.name || null,
                sale_date: saleDate,
                lines,
                fresh_qty: Number(freshQty || 0),
                fresh_value: Number(freshValue || 0),
                eoss_qty: Number(eossQty || 0),
                eoss_value: Number(eossValue || 0),
                total_qty: totalQty,
                mrp_value: Number(mrpValue || 0),
                net_value: Number(netValue || 0),
                submit: !!finalize,
            };
            const r = await http.post("/spykar/daily-sales", payload);
            toast.success(
                finalize ? "Daily sales submitted & locked" : "Draft saved",
                { description: `${outlet?.name || ""} · ${saleDate}` }
            );
            if (finalize && r.data?.total_qty === 0) {
                toast.warning("Submitted with zero sales — flagged for review");
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const s = statusPayload?.summary || {};

    return (
        <div className="mx-auto max-w-[1560px] space-y-4 p-6" data-testid="daily-sales-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="mlabel">Spykar Ops · Daily Sales Capture</div>
                    <h1 className="text-[22px] font-semibold tracking-tight">Provisional Daily Sales</h1>
                    <p className="mt-1 text-[12px] text-muted-foreground">
                        Per-outlet daily entry with draft &amp; lock. Zero must be entered explicitly (blank not allowed).
                    </p>
                </div>
                <div className="text-[11px] text-muted-foreground">
                    {statusPayload
                        ? `${statusPayload.summary.Submitted || 0}/${statusPayload.summary.total} outlets submitted on ${statusPayload.sale_date}`
                        : "…"}
                </div>
            </div>

            {/* Submission status strip */}
            {statusPayload && (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                    <Stat label="Outlets" value={fmtNum(s.total || 0)} icon={StoreIcon} />
                    <Stat label="Submitted" value={fmtNum(s.Submitted || 0)} icon={CheckCircle2} tone="green" />
                    <Stat label="Draft" value={fmtNum(s.Draft || 0)} icon={FileClock} tone="amber" />
                    <Stat label="Not Submitted" value={fmtNum(s.NotSubmitted || 0)} icon={XCircle} tone="red" />
                    <Stat label="Zero-Sale Flags" value={fmtNum(s.ZeroSales || 0)} tone="amber" />
                </div>
            )}

            {/* Capture form */}
            <div className="rounded-sm border border-border bg-card p-4" data-testid="daily-sales-form">
                <div className="mlabel mb-3">New / Update Entry</div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <Field label="Outlet">
                        <select
                            data-testid="daily-outlet-select"
                            value={outletId}
                            onChange={(e) => setOutletId(e.target.value)}
                            className="w-full rounded-sm border border-border bg-background px-2 py-1.5 text-[12px]"
                        >
                            {stores.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {`${s.code} · ${s.name} · ${s.city}`}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Sale Date">
                        <input
                            type="date"
                            data-testid="daily-date-input"
                            value={saleDate}
                            onChange={(e) => setSaleDate(e.target.value)}
                            className="w-full rounded-sm border border-border bg-background px-2 py-1.5 text-[12px]"
                        />
                    </Field>
                    <Field label="Total Qty (auto)">
                        <input
                            readOnly
                            data-testid="daily-total-qty"
                            value={totalQty}
                            className="mono-num w-full rounded-sm border border-border bg-muted/40 px-2 py-1.5 text-[12px]"
                        />
                    </Field>
                </div>

                <div className="mt-4">
                    <div className="mlabel mb-2">Category Split (Qty)</div>
                    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                        {lines.map((l, i) => (
                            <NumField
                                key={l.category}
                                testid={`daily-cat-${l.category.replace(/\s+/g, "-")}`}
                                label={l.category}
                                value={l.qty}
                                onChange={(v) => setLineQty(i, v)}
                            />
                        ))}
                    </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-3">
                    <NumField testid="daily-fresh-qty" label="Fresh Qty" value={freshQty} onChange={setFreshQty} />
                    <NumField testid="daily-eoss-qty" label="EOSS Qty" value={eossQty} onChange={setEossQty} />
                    <NumField testid="daily-mrp" label="MRP Value (₹)" value={mrpValue} onChange={setMrpValue} />
                    <NumField testid="daily-fresh-val" label="Fresh Value (₹)" value={freshValue} onChange={setFreshValue} />
                    <NumField testid="daily-eoss-val" label="EOSS Value (₹)" value={eossValue} onChange={setEossValue} />
                    <NumField testid="daily-net" label="Net Value (₹)" value={netValue} onChange={setNetValue} />
                </div>

                <div className="mt-4 flex items-center justify-end gap-2">
                    <button
                        data-testid="daily-save-draft"
                        disabled={saving}
                        onClick={() => submit(false)}
                        className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-background px-3 py-1.5 text-[12px] font-medium hover:bg-muted/40 disabled:opacity-50"
                    >
                        <Save className="h-3.5 w-3.5" /> Save Draft
                    </button>
                    <button
                        data-testid="daily-submit-lock"
                        disabled={saving}
                        onClick={() => submit(true)}
                        className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
                    >
                        <Send className="h-3.5 w-3.5" /> Submit &amp; Lock
                    </button>
                </div>
            </div>

            {/* Outlet status grid */}
            {statusPayload && (
                <div className="overflow-hidden rounded-sm border border-border bg-card" data-testid="daily-status-table">
                    <div className="border-b border-border bg-muted/40 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Submission Monitor · {statusPayload.sale_date}
                    </div>
                    <div className="max-h-[440px] overflow-auto">
                        <table className="w-full text-[12px]">
                            <thead className="sticky top-0 z-10 bg-muted/60 text-[10px] uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="px-3 py-2 text-left">Outlet</th>
                                    <th className="px-3 py-2 text-left">Code</th>
                                    <th className="px-3 py-2 text-left">City</th>
                                    <th className="px-3 py-2 text-left">Region</th>
                                    <th className="px-3 py-2 text-left">Status</th>
                                    <th className="px-3 py-2 text-right">Qty</th>
                                    <th className="px-3 py-2 text-right">Net (₹)</th>
                                    <th className="px-3 py-2 text-left">Submitted At</th>
                                </tr>
                            </thead>
                            <tbody>
                                {statusPayload.rows.map((r) => (
                                    <tr key={r.outlet_id} className="border-t border-border hover:bg-muted/40">
                                        <td className="px-3 py-1.5">{r.outlet_name}</td>
                                        <td className="mono-num px-3 py-1.5 text-muted-foreground">{r.outlet_code}</td>
                                        <td className="px-3 py-1.5">{r.city}</td>
                                        <td className="px-3 py-1.5 text-muted-foreground">{r.region}</td>
                                        <td className="px-3 py-1.5">
                                            <span
                                                className={
                                                    "rounded-sm border px-1.5 py-[1px] text-[10px] font-medium " +
                                                    (STATUS_STYLES[r.status] || "bg-muted text-muted-foreground border-border")
                                                }
                                            >
                                                {r.status}
                                            </span>
                                        </td>
                                        <td className="mono-num px-3 py-1.5 text-right">{r.total_qty ?? "—"}</td>
                                        <td className="mono-num px-3 py-1.5 text-right">
                                            {r.net_value == null ? "—" : fmtINR(r.net_value)}
                                        </td>
                                        <td className="mono-num px-3 py-1.5 text-[11px] text-muted-foreground">
                                            {r.submitted_at ? r.submitted_at.slice(0, 19).replace("T", " ") : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="mlabel mb-1">{label}</div>
            {children}
        </label>
    );
}

function NumField({ label, value, onChange, testid }) {
    return (
        <label className="block">
            <div className="mlabel mb-1">{label}</div>
            <input
                type="number"
                step="any"
                min="0"
                data-testid={testid}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="mono-num w-full rounded-sm border border-border bg-background px-2 py-1.5 text-[12px]"
            />
        </label>
    );
}

function Stat({ label, value, tone, icon: Icon }) {
    const c =
        tone === "green" ? "text-emerald-600" : tone === "amber" ? "text-amber-600" : tone === "red" ? "text-red-500" : "";
    return (
        <div className="rounded-sm border border-border bg-card p-3">
            <div className="mlabel flex items-center gap-1.5">
                {Icon && <Icon className="h-3 w-3" />}
                {label}
            </div>
            <div className={`mono-num mt-1 text-xl font-semibold ${c}`}>{value}</div>
        </div>
    );
}
